import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "You're not asking for a job, you're selling a service",
  "description": "You're not asking for a job, you're selling a service. The true question behind every interview is \"How can you help us?\"",
  "content_upgrade": "SeniorMindset",
  "categories": "Interviewing, Mindset",
  "published": "2020-11-20T00:00:00.000Z",
  "hero": "./img/screenshot-1605894339802.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`Job interviews suck. You go in there, solve a few puzzles, get a little hazing, and never hear from them again.`}</p>
    <p>{`Now consider the other side.`}</p>
    <p>{`You post a job, get 5000 applicants, pick 100, spend 2 weeks on phone screens, 5 people know what they applied for.`}</p>
    <p>{`💩`}</p>
    <p>{`Interviewing sucks for everyone.`}</p>
    <p>{`My girlfriend got a business job at Visa last year and later found that 5000 applied for her role. She stood out `}<em parentName="p">{`because she read the job description`}</em>{` and highlighted relevant experience. Yeah, that's the bar.`}</p>
    <p>{`Her boss hired `}<em parentName="p">{`both`}</em>{` candidates that stood out. They're besties now. He comes over for dinner sometimes. 🍲`}</p>
    <p>{`Out of 5000 people, `}<strong parentName="p">{`2`}</strong>{` stood out.`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/how-to-grow-as-a-senior-engineer-or-why-i-got-a-new-job"
      }}>{`I changed jobs in June`}</a>{`. A 30min chat over coffee (I was referred) impressed them enough that they followed up for months until I was ready to interview.`}</p>
    <p>{`What's the difference between the faceless masses and the few who stand out?`}</p>
    <ContentUpgrades.SeniorMindset />
    <h2 {...{
      "id": "its-how-you-present-yourself"
    }}>{`It's how you present yourself`}</h2>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`The true question behind every job interview is "How can you help us?"`}<br parentName="p"></br><br parentName="p"></br>{`Make sure you answer.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1324068647552249856"
      }}>{`November 4, 2020`}</a></blockquote>
    <p>{`Imagine you're talking to 2 candidates for a senior engineering role. Same background, same experience, same years on the job.`}</p>
    <p><strong parentName="p">{`Bob presents as a typical engineer.`}</strong>{` Amazing technical chops, can code circles around you, knows how to break complex problems into discrete tasks.`}</p>
    <p>{`Bob asks about your technical stack, chuckles when you answer, says when he worked at Google they handled that differently. You've got work to do before Bob's impressed.`}</p>
    <p><strong parentName="p">{`Alice presents as a professional engineer.`}</strong>{` Amazing technical chops, can code circles around you, knows how to break complex problems into discrete tasks. She likes to mentor junior engineers to help them advance faster.`}</p>
    <p>{`Alice asks about your technical stack and says `}<em parentName="p">{`"Yeah that's pretty typical at your stage. Usually companies get a product out quickly to test the market. Are you running into this issue yet? Ah cool, you will in a couple months, everyone does. I've seen this solution work before. How are you doing on the frontend? The mess is starting to slow you down isn't it? Here's what I've done in the past to solve that problem, we can take a step by step approach like this. You can't just abandon code that's working and making money. Oh btw what's your team scaling strategy?"`}</em></p>
    <p>{`Who do you hire?`}</p>
    <h2 {...{
      "id": "be-the-expert-have-suggestions"
    }}>{`Be the expert, have suggestions`}</h2>
    <p>{`When you're junior, coachability is what gets you hired. How quickly can we train you to be effective?`}</p>
    <p>{`When you're medium, technical chops get you hired. Can you do useful work `}<em parentName="p">{`right now`}</em>{` and quickly fill gaps in knowledge?`}</p>
    <p><a parentName="p" {...{
        "href": "https://gist.github.com/gtallen1187/e83ed02eac6cc8d7e185"
      }}>{`A little bit of slope makes up for a lot of y-intercept.`}</a></p>
    <p><a parentName="p" {...{
        "href": "https://gist.github.com/gtallen1187/e83ed02eac6cc8d7e185"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "819px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "78.47533632286996%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'315\\'%20viewBox=\\'0%200%20400%20315\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M236%2015c-4%202-3%206%202%208l4%203c-1%202-5%202-6%201-2-3-4-1-2%202%201%202%208%203%2010%201%203-4%201-7-4-9l-4-2c0-2%203-2%205-1%202%203%204%202%203-1-1-2-5-3-8-2M88%2022l-3%208c-1%202%203%201%204-1%201-3%207-3%208%200l2%202%202-1c-5-13-6-15-8-15-1%200-3%202-5%207m81%201v8h5l6-1c2-1%203-5%201-6v-4c0-4-1-5-7-5h-5v8m-41-5c-1%202-1%204%201%2011%200%201%201%202%203%202s3-1%201-3c-2-1-1-6%201-6%201%200%202%201%202%204%200%204%201%205%203%205l2-1-1-2-1-3%201-3%201-1-1-2-1-1c0-3-2-2-3%200l-2%202-2-2c0-3-3-3-4%200m78%203c-2%202-3%205%200%208%203%204%2010%202%2010-4s-6-8-10-4m50%200c-3%202-2%208%200%209%207%204%2013-4%208-9-2-2-6-3-8%200m12%206c0%206%200%208%202%208l1-2c0-2%200-3%203-2%203%200%205-2%205-6%200-5-1-6-7-6h-4v8M15%2050c0%206%200%208%202%208l1-5v-5l2%205c2%207%204%206%206-1l2-4v5c0%208%202%206%203-3v-8h-3c-2%200-3%201-3%204-2%205-2%206-3%201-1-4-2-5-4-5h-3v8m31%200c0%206%201%208%202%208l2-3c0-3%201-3%203%201l2%202c2%200%202-1%200-5-2-2-2-3-1-4l2-2c0-2-2-1-4%201-2%201-2%201-2-2s0-4-2-4-2%201-2%208m44-1c1%207%202%209%207%209s6-2%207-9c0-7%200-7-2-7s-2%201-2%206c0%206-1%207-3%207s-3-1-3-7c0-5%200-6-2-6s-2%200-2%207m-55-2c-2%201%200%203%202%202h3c1%201%200%201-2%202-7%201-6%206%201%207h5v-5l-1-6h-8m72%207c0%206%200%208%202%208l1-2c0-2%200-3%202-2%204%200%206-2%206-6%200-5-2-6-7-6h-4v8m29-6c-4%203-1%2010%204%2010%202%200%206-3%206-6%200-5-7-8-10-4m61-1c-4%206%200%2013%207%2011%204-2%205-10%200-11h-7m28%201c-3%202-2%206%200%208%204%204%2010%201%2010-4s-7-8-10-4m120%206c0%206%200%208%202%208l1-2c0-2%200-3%202-2%205%201%208-5%205-10-1-2-2-2-6-2h-4v8M52%20168v87h151a4296%204296%200%20000-2H54v-18l1-17%2022-9%2084-30%2062-22%2015-2a8664%208664%200%2001-6-1%202412%202412%200%2001112-41%203328%203328%200%2001-123%2042%204018%204018%200%2001-164%2020h-3v-47a431%20431%200%2000-2%2040m-27-63l1%201%201%201%202%203c2%201%202%201%200%203s-1%206%201%206l-1%202c-3%203-1%206%202%206%205%200%207-5%203-7v-2c3-2%202-5%200-6h-2l2-2%202-4c0-2%200-2-5-2l-6%201m163%2056a43233%2043233%200%2001-125%2014l-9%201v20l1%2019%2024-8a1319628%201319628%200%2001133-49l-24%203\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d90725cf0bb245db67ca7ad0651ea27d/ca0a1/Slope-makes-up-for-y-intercept3g909i.webp 223w", "/static/d90725cf0bb245db67ca7ad0651ea27d/75680/Slope-makes-up-for-y-intercept3g909i.webp 445w", "/static/d90725cf0bb245db67ca7ad0651ea27d/6d63c/Slope-makes-up-for-y-intercept3g909i.webp 819w"],
                "sizes": "(max-width: 819px) 100vw, 819px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d90725cf0bb245db67ca7ad0651ea27d/e92b6/Slope-makes-up-for-y-intercept3g909i.png 223w", "/static/d90725cf0bb245db67ca7ad0651ea27d/e66bf/Slope-makes-up-for-y-intercept3g909i.png 445w", "/static/d90725cf0bb245db67ca7ad0651ea27d/97655/Slope-makes-up-for-y-intercept3g909i.png 819w"],
                "sizes": "(max-width: 819px) 100vw, 819px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d90725cf0bb245db67ca7ad0651ea27d/97655/Slope-makes-up-for-y-intercept3g909i.png",
                "alt": "Slope makes up for y-intercept",
                "title": "Slope makes up for y-intercept",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Slope makes up for y-intercept`}</figcaption>{`
  `}</figure></a></p>
    <p>{`When you're senior, `}<strong parentName="p">{`your experience and opinions get you hired`}</strong>{`.`}</p>
    <p>{`The company doesn't know how to solve their problems. They don't even know the problems. They just know it hurts. Why aren't we shipping? Why's our shit broken? Why can't we scale?`}</p>
    <p><strong parentName="p">{`You`}</strong>{` are the expert my friend. You've seen this before, you know how it goes, you know what breaks before it breaks. You can see it coming a mile away and code or architect defensively.`}</p>
    <p>{`Borne out of experience, lessons learned, and battle scars, you can give the new team advice. You don't just write code, you write the right code.`}</p>
    <p>{`Or as Jonathan Stark said in a recent email: `}<strong parentName="p">{`Your value comes from what you know, not just what you do.`}</strong></p>
    <p>{`Pure knowing without doing is for VPs and CTOs. Sounds boring. 🤷‍♂️`}</p>
    <h2 {...{
      "id": "interviews-are-sales"
    }}>{`Interviews are sales`}</h2>
    <p>{`You and the hiring team are entering a $700,000 business transaction (comp over 4 years) with imperfect information. You don't know if you'll like the work and the company, they don't know if you can help.`}</p>
    <p>{`If you can't deliver and get fired in 6 months, that's $87,000 in your pocket. Good incentive for fakes, eh? Companies are careful.`}</p>
    <p>{`With that in mind, how would `}<em parentName="p">{`you`}</em>{` sell a $700,000 product? How would you buy it? 🤔`}</p>
    <p>{`You don't buy a house after a 30min chat with a realtor do you? There's a process. There's schmoozing. There's due diligence. There's followup.`}</p>
    <p>{`Details vary but an attitude of `}<em parentName="p">{`"I am selling the expensive services of a professional to a company that needs my help"`}</em>{` never fails. Read a book or two on sales. It helps.`}</p>
    <p>{`SPiN Selling is a good place to start.`}</p>
    <ol>
      <li parentName="ol">{`Identify their pain points`}</li>
      <li parentName="ol">{`Sketch out solutions`}</li>
      <li parentName="ol">{`Make suggestions`}</li>
    </ol>
    <p>{`Make the interview valuable. After each chat, the interviewer should think: `}<em parentName="p">{`"That was amazing! I can't wait to implement those recommendations even if you don't get hired!"`}</em></p>
    <p>{`You're not asking for a job, you're selling a service. A professional never begs my friend. ❤️`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: and remember, the interviewer wants you to succeed. Means they can stop interviewing and get back to work.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      